/**
 * Bespoke page allowlist — Task 50.
 *
 * Every page that bypasses the generic renderer must be registered here.
 * No page may bypass this allowlist.
 *
 * Fields:
 * - routeId: stable route ID
 * - component: the bespoke component
 * - owner: owning team
 * - reason: why the generic renderer cannot express this page
 * - regulated: whether this is a regulated/compliance surface
 * - migrationTarget: generic descriptor to migrate to (if planned)
 * - reviewDate: date this entry should be reviewed for migration
 */
import { ROUTE_IDS } from '@finance/contracts';
import type { RouteId } from '@finance/contracts';

export interface BespokePageEntry {
  routeId: RouteId;
  component: () => Promise<{ default: React.ComponentType }>;
  owner: string;
  reason: string;
  regulated: boolean;
  migrationTarget?: string;
  reviewDate: string;
}

/**
 * Allowlisted bespoke pages.
 *
 * Authorization pages, MFA login challenge flows, and the dashboard are registered here
 * because they have specialized state machines or rich interactive components
 * that the generic PageRenderer cannot yet express.
 */
export const BESPOKE_PAGE_ALLOWLIST: BespokePageEntry[] = [
  {
    routeId: ROUTE_IDS.HOLDINGS,
    component: () =>
      import('../../routes/holdings/HoldingsRoutePage').then(m => ({
        default: m.HoldingsRoutePage,
      })),
    owner: 'investment',
    reason:
      'The holdings overview is an organization-level alias for the canonical data-holdings resource surface.',
    regulated: true,
    migrationTarget: 'resource.holdings',
    reviewDate: '2027-Q1',
  },
  {
    routeId: ROUTE_IDS.DATA_POSITION_TRANSFERS,
    component: () =>
      import('../../routes/transfers/PositionTransfersPage').then(m => ({
        default: m.PositionTransfersPage,
      })),
    owner: 'investment',
    reason:
      'Position-transfer workflows require lot-allocation lineage, maker-checker lifecycle actions, and explicit non-cash side-effect disclosure.',
    regulated: true,
    reviewDate: '2027-Q1',
  },
  {
    routeId: ROUTE_IDS.DATA_MANAGEMENT_FEES,
    component: () =>
      import('../../routes/accounting/ManagementFeesPage').then(m => ({
        default: m.ManagementFeesPage,
      })),
    owner: 'accounting',
    reason: 'Management fee eligibility is server-projected.',
    regulated: true,
    reviewDate: '2027-Q1',
  },
  {
    routeId: ROUTE_IDS.DATA_PERFORMANCE_FEES,
    component: () =>
      import('../../routes/accounting/PerformanceFeesPage').then(m => ({
        default: m.PerformanceFeesPage,
      })),
    owner: 'accounting',
    reason:
      'Performance-fee calculation consumes only server-projected finalized inputs and an independent approval lifecycle.',
    regulated: true,
    reviewDate: '2027-Q1',
  },
  {
    routeId: ROUTE_IDS.DATA_SETTLEMENT_INSTRUCTIONS,
    component: () =>
      import('../../routes/settlement/SettlementInstructionsPage').then(m => ({
        default: m.SettlementInstructionsPage,
      })),
    owner: 'investment',
    reason:
      'Settlement lifecycle controls require AAL2 maker-checker actions and availability binding not expressible by the generic resource renderer.',
    regulated: true,
    reviewDate: '2027-Q1',
  },
  {
    routeId: ROUTE_IDS.DATA_SETTLEMENT_FAILURES,
    component: () =>
      import('../../routes/settlement/SettlementInstructionsPage').then(m => ({
        default: m.SettlementInstructionsPage,
      })),
    owner: 'investment',
    reason:
      'Failed settlement review is a status-filtered lifecycle view with correction controls.',
    regulated: true,
    reviewDate: '2027-Q1',
  },
  {
    routeId: ROUTE_IDS.LOGIN,
    component: () => import('../../routes/auth/LoginPage').then(m => ({ default: m.LoginPage })),
    owner: 'platform-auth',
    reason: 'Multi-step authentication flow with challenge/context-selection state machine',
    regulated: true,
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.FORGOT_PASSWORD,
    component: () =>
      import('../../routes/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })),
    owner: 'platform-auth',
    reason: 'Password-reset email flow with token link handling',
    regulated: true,
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.RESET_PASSWORD,
    component: () =>
      import('../../routes/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })),
    owner: 'platform-auth',
    reason: 'Token-validated password reset form',
    regulated: true,
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.MFA_VERIFY,
    component: () =>
      import('../../routes/auth/MfaVerifyPage').then(m => ({ default: m.MfaVerifyPage })),
    owner: 'platform-auth',
    reason: 'TOTP verification with AAL2 elevation — specialized security flow',
    regulated: true,
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.INITIAL_PASSWORD_CHANGE,
    component: () =>
      import('../../routes/auth/InitialPasswordChangePage').then(m => ({
        default: m.InitialPasswordChangePage,
      })),
    owner: 'platform-auth',
    reason: 'Forced password change on first login — specialized security flow',
    regulated: true,
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.MFA_ENROLL,
    component: () =>
      import('../../routes/auth/MfaEnrollPage').then(m => ({ default: m.MfaEnrollPage })),
    owner: 'platform-auth',
    reason: 'QR code display and backup code confirmation — requires media rendering',
    regulated: true,
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.MFA_STEP_UP,
    component: () =>
      import('../../routes/auth/MfaStepUpPage').then(m => ({ default: m.MfaStepUpPage })),
    owner: 'platform-auth',
    reason: 'Focused MFA step-up page for AAL2 privilege elevation from LOCKED_AAL actions',
    regulated: true,
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.DASHBOARD,
    component: () =>
      import('../../routes/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })),
    owner: 'investment-ux',
    reason: 'Portfolio dashboard with rich KPI strip, charts, and drill-down actions',
    regulated: false,
    migrationTarget: 'page.investment.dashboard.generic',
    reviewDate: '2027-Q1',
  },
  {
    routeId: ROUTE_IDS.TRANSACTIONS,
    component: () =>
      import('../../routes/transactions/TransactionListPage').then(m => ({
        default: m.TransactionListPage,
      })),
    owner: 'investment-ux',
    reason: 'Complex filter bar and workflow action descriptors pending generic table support',
    regulated: true,
    migrationTarget: 'page.investment.transaction.list.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.TRANSACTION_APPROVALS,
    component: () =>
      import('../../routes/transactions/TransactionApprovalsPage').then(m => ({
        default: m.TransactionApprovalsPage,
      })),
    owner: 'investment-ux',
    reason:
      'Approval actions are governed by the transaction lifecycle table and require the specialized workflow renderer.',
    regulated: true,
    migrationTarget: 'page.investment.transaction.approvals.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.TRANSACTION_REVERSALS,
    component: () =>
      import('../../routes/transactions/TransactionReversalsPage').then(m => ({
        default: m.TransactionReversalsPage,
      })),
    owner: 'investment-ux',
    reason:
      'Reversal, repair, and repost actions are stateful lifecycle commands rendered by the shared transaction table.',
    regulated: true,
    migrationTarget: 'page.investment.transaction.reversals.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.TRANSACTION_CREATE,
    component: () =>
      import('../../routes/transactions/TransactionFormPage').then(m => ({
        default: m.TransactionFormPage,
      })),
    owner: 'investment-ux',
    reason: 'Multi-field transaction creation with instrument/lot selectors',
    regulated: true,
    migrationTarget: 'page.investment.transaction.form.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.TRANSACTION_DETAIL,
    component: () =>
      import('../../routes/transactions/TransactionDetailPage').then(m => ({
        default: m.TransactionDetailPage,
      })),
    owner: 'investment-ux',
    reason: 'Transaction detail with lifecycle actions and audit trail',
    regulated: true,
    migrationTarget: 'page.investment.transaction.detail.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.SETTINGS,
    component: () =>
      import('../../routes/settings/SettingsPage').then(m => ({ default: m.SettingsPage })),
    owner: 'platform-ux',
    reason: 'Settings hub with navigation to profile/password/preferences',
    regulated: false,
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.SETTINGS_PROFILE,
    component: () =>
      import('../../routes/settings/ProfilePage').then(m => ({ default: m.ProfilePage })),
    owner: 'platform-ux',
    reason: 'User profile editing form',
    regulated: false,
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.SETTINGS_PREFERENCES,
    component: () =>
      import('../../routes/settings/PreferencesPage').then(m => ({ default: m.PreferencesPage })),
    owner: 'platform-ux',
    reason: 'Locale, calendar, and notification preferences',
    regulated: false,
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.SETTINGS_MFA,
    component: () =>
      import('../../routes/settings/MfaSettingsPage').then(m => ({ default: m.MfaSettingsPage })),
    owner: 'platform-ux',
    reason: 'MFA enrollment, verification, recovery-code rotation, and disablement',
    regulated: true,
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.SETTINGS_PASSWORD,
    component: () =>
      import('../../routes/settings/ChangePasswordPage').then(m => ({
        default: m.ChangePasswordPage,
      })),
    owner: 'platform-ux',
    reason: 'Authenticated password change with current-password and TOTP verification',
    regulated: true,
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.SETTINGS_SESSIONS,
    component: () =>
      import('../../routes/settings/SessionsPage').then(m => ({ default: m.SessionsPage })),
    owner: 'platform-ux',
    reason: 'Active session listing and per-session revocation',
    regulated: true,
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.SETTINGS_SECURITY,
    component: () =>
      import('../../routes/settings/SecurityOverviewPage').then(m => ({
        default: m.SecurityOverviewPage,
      })),
    owner: 'platform-ux',
    reason: 'Security overview landing page (credential source, MFA status, sessions summary)',
    regulated: true,
    reviewDate: '2026-Q4',
  },
  // UX-ARCH-008: ADMIN_HUB, DATA_HUB, PLATFORM_ADMIN_HUB, and ORGANIZATION_ADMIN_HUB
  // removed from bespoke allowlist — handled by generic HubRoutePage in router.
  // The router checks bespoke first, then hub routes, so having hubs here creates
  // duplicate renderer ownership. These routes now have exactly one renderer authority.
  {
    routeId: ROUTE_IDS.USERS,
    component: () =>
      import('../../routes/admin/UserManagementPage').then(m => ({
        default: m.UserManagementPage,
      })),
    owner: 'platform-ux',
    reason: 'User management table with create/edit/role actions',
    regulated: true,
    migrationTarget: 'page.users.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.ROLES,
    component: () =>
      import('../../routes/admin/RoleManagementPage').then(m => ({
        default: m.RoleManagementPage,
      })),
    owner: 'platform-ux',
    reason: 'Role management table and permission matrix',
    regulated: true,
    migrationTarget: 'page.roles.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.USERS_CREATE,
    component: () =>
      import('../../routes/admin/UserCreatePage').then(m => ({ default: m.UserCreatePage })),
    owner: 'platform-ux',
    reason:
      'Full-page user creation form using the AdminFormPage shell; migrate once PageRenderer FORM sections support submission lifecycle hooks',
    regulated: true,
    migrationTarget: 'page.users.create.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.USERS_EDIT,
    component: () =>
      import('../../routes/admin/UserEditPage').then(m => ({ default: m.UserEditPage })),
    owner: 'platform-ux',
    reason:
      'Full-page user edit form using the AdminFormPage shell; migrate once PageRenderer FORM sections support submission lifecycle hooks',
    regulated: true,
    migrationTarget: 'page.users.edit.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.ROLES_CREATE,
    component: () =>
      import('../../routes/admin/RoleCreatePage').then(m => ({ default: m.RoleCreatePage })),
    owner: 'platform-ux',
    reason:
      'Full-page role creation form using the AdminFormPage shell; migrate once PageRenderer FORM sections support submission lifecycle hooks',
    regulated: true,
    migrationTarget: 'page.roles.create.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.ROLES_EDIT,
    component: () =>
      import('../../routes/admin/RoleEditPage').then(m => ({ default: m.RoleEditPage })),
    owner: 'platform-ux',
    reason:
      'Full-page role edit form using the AdminFormPage shell; migrate once PageRenderer FORM sections support submission lifecycle hooks',
    regulated: true,
    migrationTarget: 'page.roles.edit.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.ROLES_DETAIL,
    component: () =>
      import('../../routes/admin/RoleEditPage').then(m => ({ default: m.RoleEditPage })),
    owner: 'platform-ux',
    reason:
      'Role detail route renders the edit form as the canonical read-write surface until a dedicated detail renderer is promoted',
    regulated: true,
    migrationTarget: 'page.roles.detail.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.AUDIT_EXPLORER,
    component: () =>
      import('../../routes/admin/AuditLogPage').then(m => ({ default: m.AuditLogPage })),
    owner: 'platform-ux',
    reason: 'Audit log viewer with filters and export',
    regulated: true,
    migrationTarget: 'page.audit.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.ORGANIZATION_USERS_LIST,
    component: () =>
      import('../../routes/admin/UserManagementPage').then(m => ({
        default: m.UserManagementPage,
      })),
    owner: 'platform-ux',
    reason: 'Organization-scoped user list with branch-aware context selection',
    regulated: true,
    migrationTarget: 'page.users.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.ORGANIZATION_ROLES_LIST,
    component: () =>
      import('../../routes/admin/RoleManagementPage').then(m => ({
        default: m.RoleManagementPage,
      })),
    owner: 'platform-ux',
    reason: 'Organization-scoped role list with permission matrix',
    regulated: true,
    migrationTarget: 'page.roles.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.ORGANIZATION_USERS_CREATE,
    component: () =>
      import('../../routes/admin/UserCreatePage').then(m => ({ default: m.UserCreatePage })),
    owner: 'platform-ux',
    reason: 'Organization-scoped user creation form using the AdminFormPage shell',
    regulated: true,
    migrationTarget: 'page.organization.users.create.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.ORGANIZATION_USERS_EDIT,
    component: () =>
      import('../../routes/admin/UserEditPage').then(m => ({ default: m.UserEditPage })),
    owner: 'platform-ux',
    reason: 'Organization-scoped user edit form using the AdminFormPage shell',
    regulated: true,
    migrationTarget: 'page.organization.users.edit.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.ORGANIZATION_ROLES_CREATE,
    component: () =>
      import('../../routes/admin/RoleCreatePage').then(m => ({ default: m.RoleCreatePage })),
    owner: 'platform-ux',
    reason: 'Organization-scoped role creation form using the AdminFormPage shell',
    regulated: true,
    migrationTarget: 'page.organization.roles.create.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.ORGANIZATION_ROLES_EDIT,
    component: () =>
      import('../../routes/admin/RoleEditPage').then(m => ({ default: m.RoleEditPage })),
    owner: 'platform-ux',
    reason: 'Organization-scoped role edit form using the AdminFormPage shell',
    regulated: true,
    migrationTarget: 'page.organization.roles.edit.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.ORGANIZATION_PERMISSION_ASSIGNMENTS_LIST,
    component: () =>
      import('../../routes/admin/PermissionAssignmentListPage').then(m => ({
        default: m.PermissionAssignmentListPage,
      })),
    owner: 'platform-ux',
    reason:
      'Permission assignment reuses the canonical role and permission authority without duplicating persistence.',
    regulated: true,
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.ORGANIZATION_PERMISSION_ASSIGNMENTS_EDIT,
    component: () =>
      import('../../routes/admin/PermissionAssignmentEditPage').then(m => ({
        default: m.PermissionAssignmentEditPage,
      })),
    owner: 'platform-ux',
    reason:
      'Role permission edits share canonical concurrency, authorization, audit, and session invalidation behavior.',
    regulated: true,
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.ORGANIZATION_AUDIT_LIST,
    component: () =>
      import('../../routes/admin/AuditLogPage').then(m => ({ default: m.AuditLogPage })),
    owner: 'platform-ux',
    reason: 'Organization-scoped audit log viewer with filters and export',
    regulated: true,
    migrationTarget: 'page.audit.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.ORGANIZATION_AUDIT_DETAIL,
    component: () =>
      import('../../routes/admin/AuditLogDetailPage').then(m => ({
        default: m.AuditLogDetailPage,
      })),
    owner: 'platform-ux',
    reason: 'Read-only audit-event detail with server-redacted payload fields',
    regulated: true,
    migrationTarget: 'page.audit.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.PLATFORM_ACCESS_INSPECTOR,
    component: () =>
      import('../../routes/admin/PlatformAccessInspectorPage').then(m => ({
        default: m.PlatformAccessInspectorPage,
      })),
    owner: 'platform-security',
    reason: 'Platform-scoped access inspection diagnostics endpoint and JSON evidence view',
    regulated: true,
    migrationTarget: 'page.admin.platformAccessInspector.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.ORGANIZATION_ACCESS_INSPECTOR,
    component: () =>
      import('../../routes/admin/OrganizationAccessInspectorPage').then(m => ({
        default: m.OrganizationAccessInspectorPage,
      })),
    owner: 'platform-security',
    reason: 'Organization-scoped access inspection diagnostics endpoint and JSON evidence view',
    regulated: true,
    migrationTarget: 'page.admin.organizationAccessInspector.generic',
    reviewDate: '2026-Q4',
  },
  // UX-ARCH-008: Admin Hub routes removed from bespoke allowlist.
  // ADMIN_HUB, PLATFORM_ADMIN_HUB, ORGANIZATION_ADMIN_HUB are handled by generic HubRoutePage.
  // Data Hub route removed — DATA_HUB is handled by generic HubRoutePage.
  {
    routeId: ROUTE_IDS.ORGANIZATION_GOVERNANCE_WORKSPACE,
    component: () =>
      import('../../routes/admin/OrganizationGovernanceWorkspace').then(m => ({
        default: m.OrganizationGovernanceWorkspace,
      })),
    owner: 'platform-governance',
    reason:
      'The regulated workspace coordinates seven policy-driven lifecycle APIs, exact command schemas, evidence projections, and maker-checker actions.',
    regulated: true,
    migrationTarget: 'page.organization.governance.workspace.generic',
    reviewDate: '2027-Q1',
  },
  {
    routeId: ROUTE_IDS.ORGANIZATION_PROFILE,
    component: () =>
      import('../../routes/admin/OrganizationDetailPage').then(m => ({
        default: m.OrganizationDetailPage,
      })),
    owner: 'platform-ux',
    reason: 'Organization profile detail with editable fields and branch-aware context selection',
    regulated: false,
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.DATA_JOURNAL_CREATE,
    component: () =>
      import('../../routes/JournalCreatePage').then(m => ({ default: m.JournalCreatePage })),
    owner: 'investment-ux',
    reason:
      'Double-entry journal creation requires a dynamic balanced-line editor, which the generic form descriptor cannot express.',
    regulated: true,
    migrationTarget: 'page.data.journals.create.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.ORGANIZATION_PROFILE_DETAIL,
    component: () =>
      import('../../routes/admin/OrganizationDetailPage').then(m => ({
        default: m.OrganizationDetailPage,
      })),
    owner: 'platform-ux',
    reason: 'Organization profile detail view with scoped context selection',
    regulated: false,
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.USERS_DETAIL,
    component: () =>
      import('../../routes/admin/UserDetailPage').then(m => ({ default: m.UserDetailPage })),
    owner: 'platform-ux',
    reason:
      'User detail view using GenericResourceFormPage view mode; migrate once PageRenderer FORM sections support view mode',
    regulated: true,
    migrationTarget: 'page.users.detail.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.PLATFORM_HEALTH,
    component: () =>
      import('../../routes/admin/PlatformHealthPage').then(m => ({
        default: m.PlatformHealthPage,
      })),
    owner: 'platform-ux',
    reason: 'Platform health dashboard with status indicators and component breakdown',
    regulated: false,
    migrationTarget: 'page.platform.health.generic',
    reviewDate: '2026-Q4',
  },
  {
    routeId: ROUTE_IDS.ORGANIZATION_CLIENTS_CREATE,
    component: () =>
      import('../../routes/admin/ClientCreatePage').then(m => ({
        default: m.ClientCreatePage,
      })),
    owner: 'investment',
    reason: 'Closable dialog with multi-section investor profile layout; consistent with opening cutover pattern',
    regulated: true,
    migrationTarget: 'page.clients.create.generic-dialog',
    reviewDate: '2027-Q1',
  },
  {
    routeId: ROUTE_IDS.ORGANIZATION_PORTFOLIOS_CREATE,
    component: () =>
      import('../../routes/admin/PortfolioCreatePage').then(m => ({
        default: m.PortfolioCreatePage,
      })),
    owner: 'investment',
    reason: 'Closable dialog with client-linkage + portfolio-details sections; consistent with opening cutover pattern',
    regulated: true,
    migrationTarget: 'page.portfolios.create.generic-dialog',
    reviewDate: '2027-Q1',
  },
  {
    routeId: ROUTE_IDS.ADMIN_OPENING_CUTOVER_CREATE,
    component: () =>
      import('../../routes/admin/OpeningCutoverCreatePage').then(m => ({
        default: m.OpeningCutoverCreatePage,
      })),
    owner: 'accounting',
    reason:
      'Guided multi-step workflow for opening cutover data entry; uses SteppedFormRenderer with the canonical form descriptor',
    regulated: true,
    migrationTarget: 'page.openingCutover.create.stepped-generic',
    reviewDate: '2027-Q1',
  },
  {
    routeId: ROUTE_IDS.ADMIN_FISCAL_YEARS_LIST,
    component: () =>
      import('../../routes/admin/FiscalYearsListPage').then(m => ({
        default: m.FiscalYearsListPage,
      })),
    owner: 'accounting',
    reason: 'Fiscal year list with lifecycle actions (close, reopen); uses generic resource list backed by fiscal-years descriptor',
    regulated: true,
    migrationTarget: 'page.fiscalYears.list.generic',
    reviewDate: '2027-Q1',
  },
  {
    routeId: ROUTE_IDS.ADMIN_FISCAL_YEAR_CREATE,
    component: () =>
      import('../../routes/admin/FiscalYearCreatePage').then(m => ({
        default: m.FiscalYearCreatePage,
      })),
    owner: 'accounting',
    reason: 'Fiscal year create form; backed by backend-owned form descriptor from the experience resolver',
    regulated: true,
    migrationTarget: 'page.fiscalYears.create.generic',
    reviewDate: '2027-Q1',
  },
  {
    routeId: ROUTE_IDS.ADMIN_FISCAL_YEAR_DETAIL,
    component: () =>
      import('../../routes/admin/FiscalYearDetailPage').then(m => ({
        default: m.FiscalYearDetailPage,
      })),
    owner: 'accounting',
    reason: 'Fiscal year detail view with embedded periods table and lifecycle actions',
    regulated: true,
    migrationTarget: 'page.fiscalYears.detail.generic',
    reviewDate: '2027-Q1',
  },
  {
    routeId: ROUTE_IDS.ADMIN_FISCAL_YEAR_PERIODS,
    component: () =>
      import('../../routes/admin/FiscalYearPeriodsPage').then(m => ({
        default: m.FiscalYearPeriodsPage,
      })),
    owner: 'accounting',
    reason: 'Fiscal year periods list view; uses generic resource list backed by fiscal-periods descriptor scoped to the fiscal year ID',
    regulated: true,
    migrationTarget: 'page.fiscalYears.periods.generic',
    reviewDate: '2027-Q1',
  },
  // UX-ARCH-008: Data resource routes removed from bespoke allowlist.
  // These routes are now handled by the generic ResourceRoutePage via HUB_RESOURCE_REGISTRY.
  // The following routes were removed:
  // - DATA_INSTRUMENTS, DATA_INSTRUMENT_DETAIL (instruments)
  // - DATA_CORPORATE_ACTIONS, DATA_CORPORATE_ACTION_DETAIL (corporate-actions)
  // - DATA_FACT_BATCHES, DATA_FACT_BATCH_DETAIL (fact-batches)
  // - DATA_IMPORTS, DATA_IMPORT_DETAIL (import-jobs)
  // - DATA_RECONCILIATION, DATA_RECONCILIATION_DETAIL (reconciliation-runs)
];

/**
 * Returns true if the given routeId is registered as bespoke.
 */
export function isBespokeRoute(routeId: string): boolean {
  return BESPOKE_PAGE_ALLOWLIST.some(entry => entry.routeId === routeId);
}

/**
 * Returns the bespoke entry for a route, or undefined if not registered.
 */
export function getBespokeEntry(routeId: string): BespokePageEntry | undefined {
  return BESPOKE_PAGE_ALLOWLIST.find(entry => entry.routeId === routeId);
}
